import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "@/lib/utils"

interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  showValueLabel?: boolean
  valueLabel?: string
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({ className, showValueLabel, valueLabel, ...props }, ref) => {
  // Display the first value as a default if using value labels
  const displayValue = props.value ? props.value[0] : 0
  
  // Ensure values are valid numbers in range and aligned with step size
  const safeValue = React.useMemo(() => {
    if (!props.value || props.value.length === 0) return [0];
    return props.value.map(v => {
      if (typeof v !== 'number' || isNaN(v)) return 0;
      // Clamp the value to the valid range
      const clampedValue = Math.max(props.min || 0, Math.min(props.max || 100, v));
      // If step is defined, round to nearest step
      if (props.step) {
        const steps = Math.round(clampedValue / props.step);
        return steps * props.step;
      }
      return clampedValue;
    });
  }, [props.value, props.min, props.max, props.step]);
  
  return (
    <div className="relative">
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center",
          className
        )}
        {...props}
        value={safeValue}
      >
        <SliderPrimitive.Track className="relative h-3 w-full grow overflow-hidden rounded-full bg-secondary">
          <SliderPrimitive.Range className="absolute h-full bg-primary" />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className="block h-6 w-6 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:scale-110 hover:bg-primary/10 active:scale-95 transition-all" />
      </SliderPrimitive.Root>
      
      {showValueLabel && (
        <div className="absolute -right-1 -top-6 text-xs font-medium">
          {valueLabel ? `${displayValue}${valueLabel}` : displayValue}
        </div>
      )}
    </div>
  )
})
Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }